import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react'
import { Dialog, Flex, IconButton, Text } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import cn from 'classnames'

interface ImageLightboxProps {
  /** Display urls, in order; an entry is null when that image failed to sign. */
  images: (string | null)[]
  openIndex: number | null
  onOpenChange: (index: number | null) => void
  footer?: ReactNode
}

const MAX_SCALE = 8
// Wheel delta to scale exponent. Roughly doubles the image per notch of a mouse wheel.
const ZOOM_RATE = 0.002
const LINE_HEIGHT_PX = 16

interface Zoom {
  scale: number
  x: number
  y: number
}

const NO_ZOOM: Zoom = { scale: 1, x: 0, y: 0 }

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const transformOf = ({ x, y, scale }: Zoom): string => `translate(${x}px, ${y}px) scale(${scale})`

/** Leaves ⌘/ctrl/shift/alt clicks to the browser, so the anchor still opens in a new tab. */
export const openOnPlainClick =
  (open: () => void) =>
  (event: ReactMouseEvent<HTMLAnchorElement>): void => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    open()
  }

export const ImageLightbox = ({ images, openIndex, onOpenChange, footer }: ImageLightboxProps) => {
  // `?? null`, because an index past the end reads as undefined — which `open` below would
  // treat as an image and render a broken frame for one commit.
  const image = openIndex === null ? null : (images[openIndex] ?? null)
  const frameRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const drag = useRef<{ x: number; y: number; maxX: number; maxY: number } | null>(null)
  const [zoom, setZoom] = useState<Zoom>(NO_ZOOM)
  // Mirrors the state so the wheel listener, attached once, reads the live value.
  const zoomRef = useRef(zoom)

  // How far the image can travel before its edge would enter the frame.
  const panBounds = useCallback((scale: number): { maxX: number; maxY: number } => {
    const frame = frameRef.current
    const img = imageRef.current
    if (!frame || !img) return { maxX: 0, maxY: 0 }
    return {
      maxX: Math.max(0, (img.offsetWidth * scale - frame.clientWidth) / 2),
      maxY: Math.max(0, (img.offsetHeight * scale - frame.clientHeight) / 2)
    }
  }, [])

  const applyZoom = useCallback((next: Zoom) => {
    zoomRef.current = next
    setZoom(next)
  }, [])

  // Deleting the open image leaves the index past the end; close rather than hold a stale one.
  useEffect(() => {
    if (openIndex !== null && image === null) onOpenChange(null)
  }, [image, openIndex, onOpenChange])

  useEffect(() => {
    applyZoom(NO_ZOOM)
  }, [openIndex, applyZoom])

  useEffect(() => {
    if (openIndex === null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') onOpenChange((openIndex + images.length - 1) % images.length)
      if (event.key === 'ArrowRight') onOpenChange((openIndex + 1) % images.length)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [images.length, openIndex, onOpenChange])

  // A ref callback, not an effect: the dialog content mounts a render after `open` flips, so
  // an effect keyed on `openIndex` runs while the frame is still absent and never re-runs.
  // Listening directly also keeps the handler non-passive — React registers wheel passively
  // at the root, where preventDefault is a no-op.
  const attachFrame = useCallback(
    (frame: HTMLDivElement | null) => {
      frameRef.current = frame
      if (!frame) return

      const onWheel = (event: WheelEvent) => {
        event.preventDefault()
        const rect = frame.getBoundingClientRect()
        // Cursor relative to the frame's centre, which is the transform origin.
        const cursorX = event.clientX - rect.left - rect.width / 2
        const cursorY = event.clientY - rect.top - rect.height / 2
        const delta = event.deltaMode === 1 ? event.deltaY * LINE_HEIGHT_PX : event.deltaY
        const current = zoomRef.current
        const scale = clamp(current.scale * Math.exp(-delta * ZOOM_RATE), 1, MAX_SCALE)
        if (scale === current.scale) return
        if (scale === 1) {
          applyZoom(NO_ZOOM)
          return
        }
        // Holds whatever sits under the cursor still while the rest grows around it.
        const ratio = scale / current.scale
        const { maxX, maxY } = panBounds(scale)
        applyZoom({
          scale,
          x: clamp(cursorX - (cursorX - current.x) * ratio, -maxX, maxX),
          y: clamp(cursorY - (cursorY - current.y) * ratio, -maxY, maxY)
        })
      }

      frame.addEventListener('wheel', onWheel, { passive: false })
      return () => {
        frame.removeEventListener('wheel', onWheel)
        frameRef.current = null
      }
    },
    [applyZoom, panBounds]
  )

  const step = (offset: number) => {
    if (openIndex === null) return
    onOpenChange((openIndex + images.length + offset) % images.length)
  }

  const startPan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const { scale } = zoomRef.current
    if (scale === 1) return
    event.currentTarget.setPointerCapture(event.pointerId)
    // Bounds are fixed for the gesture: only the transform moves, and that does not affect layout.
    drag.current = { x: event.clientX, y: event.clientY, ...panBounds(scale) }
  }

  const pan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const from = drag.current
    if (!from) return
    const current = zoomRef.current
    const next: Zoom = {
      scale: current.scale,
      x: clamp(current.x + (event.clientX - from.x), -from.maxX, from.maxX),
      y: clamp(current.y + (event.clientY - from.y), -from.maxY, from.maxY)
    }
    from.x = event.clientX
    from.y = event.clientY
    zoomRef.current = next
    // Written to the node rather than to state: a re-render per pointermove would re-render the
    // whole dialog to change one transform. Committed to state on pointer-up.
    if (imageRef.current) imageRef.current.style.transform = transformOf(next)
  }

  const endPan = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    setZoom(zoomRef.current)
  }

  const isGallery = images.length > 1
  const isZoomed = zoom.scale > 1

  return (
    <Dialog.Root open={image !== null} onOpenChange={(open) => (open ? undefined : onOpenChange(null))}>
      <Dialog.Content maxWidth="min(90vw, 1100px)">
        <Dialog.Title size="3">
          {isGallery ? `Photo ${(openIndex ?? 0) + 1} / ${images.length}` : 'Photo'}
        </Dialog.Title>
        <Flex align="center" gap="3" mt="3">
          {isGallery ? (
            <IconButton variant="soft" color="gray" onClick={() => step(-1)} aria-label="Previous photo">
              <ChevronLeftIcon />
            </IconButton>
          ) : null}
          <Flex
            ref={attachFrame}
            align="center"
            justify="center"
            onPointerDown={startPan}
            onPointerMove={pan}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            onDoubleClick={() => applyZoom(NO_ZOOM)}
            className={cn(
              'h-[70vh] min-w-0 flex-1 touch-none overflow-hidden rounded-[var(--radius-3)] bg-[var(--gray-2)]',
              isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
            )}
          >
            {image ? (
              <img
                ref={imageRef}
                src={image}
                alt="Installation"
                draggable={false}
                className="max-h-full max-w-full select-none object-contain"
                style={{ transform: transformOf(zoom) }}
              />
            ) : (
              <Text size="2" color="gray">
                Failed to load
              </Text>
            )}
          </Flex>
          {isGallery ? (
            <IconButton variant="soft" color="gray" onClick={() => step(1)} aria-label="Next photo">
              <ChevronRightIcon />
            </IconButton>
          ) : null}
        </Flex>
        {footer ? <Flex mt="3">{footer}</Flex> : null}
      </Dialog.Content>
    </Dialog.Root>
  )
}
